import { nodeConfig } from '@scripts/mixins';
import deeplink, { updateHash } from '@scripts/helpers/deeplink';
import { toggleHtmlClass } from '@scripts/helpers/utils';
import * as CONSTANTS from '@scripts/helpers/constants';

export const NAMESPACE = 'popover';
export const DEFAULT_SELECTOR = '[data-js-popover]';
const CLOSE_SELECTOR = '[data-js-popover-close]';

/**
 * @typedef {Object} PopoverSettings
 * @property {boolean} [hover=false] - Open on hover with 150ms close debounce.
 * @property {boolean} [closeOnBlur=true] - Close on focus loss.
 * @property {boolean} [deeplink=false] - Enable URL hash updates.
 * @property {string|null} [htmlStateClass=null] - Class added to <html> when open.
 */
export const DEFAULT_SETTINGS = {
	hover: false,
	closeOnBlur: true,
	deeplink: false,
	htmlStateClass: null
};

/**
 * Native Popover API wrapper.
 * @fires popover:open
 * @fires popover:close
 */
export class Popover {
	/**
	 * @param {HTMLElement} node - The popover panel element.
	 * @param {PopoverSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;
		this.elTriggers = [];
		this.hoverTimeout = null;
	}

	init() {
		this._setup();
		this._bindUI();

		if (this.config.deeplink) {
			this._setupDeepLinks();
		}
	}

	/** @returns {boolean} */
	isOpen() {
		return this.elNode.matches(':popover-open');
	}

	toggle() {
		this.isOpen() ? this.close() : this.open();
	}

	open() {
		if (this.isOpen()) return;

		this._clearHoverTimeout();
		this.elNode.showPopover();
	}

	close() {
		if (!this.isOpen()) return;

		this.elNode.hidePopover();
	}

	_setup() {
		if (!this.elNode.id) return;

		this.elTriggers = Array.from(
			document.querySelectorAll(`[popovertarget="${this.elNode.id}"]`)
		);
	}

	_setupDeepLinks() {
		if (!this.elNode.id) return;

		deeplink.register(`${DEFAULT_SELECTOR}#${this.elNode.id}`, () => {
			if (this.isOpen()) return;

			this.open();
			this.elNode.focus();
		});
	}

	/** @param {boolean} isOpen */
	_updateTriggerState(isOpen) {
		this.elTriggers.forEach((trigger) => {
			trigger.classList.toggle(CONSTANTS.ACTIVE_CLASS, isOpen);
		});
	}

	/** @param {string} action */
	_dispatch(action) {
		this.elNode.dispatchEvent(
			new CustomEvent(`${NAMESPACE}:${action}`, {
				bubbles: true,
				detail: { instance: this }
			})
		);
	}

	_bindUI() {
		this._onToggle = this._onToggle.bind(this);
		this._onCloserClick = this._onCloserClick.bind(this);
		this._onBlurCheck = this._onBlurCheck.bind(this);

		this.elNode.addEventListener('toggle', this._onToggle);
		this.elNode.addEventListener('click', this._onCloserClick);

		if (this.config.hover) {
			this._onTriggerClick = this._onTriggerClick.bind(this);
			this._onMouseEnter = this._onMouseEnter.bind(this);
			this._onMouseLeave = this._onMouseLeave.bind(this);

			this.elTriggers.forEach((trigger) => {
				trigger.addEventListener('click', this._onTriggerClick);
				trigger.addEventListener('mouseenter', this._onMouseEnter);
				trigger.addEventListener('mouseleave', this._onMouseLeave);
			});

			this.elNode.addEventListener('mouseenter', this._onMouseEnter);
			this.elNode.addEventListener('mouseleave', this._onMouseLeave);
		}

		if (this.config.closeOnCalendarChange) {
			this.elNode.addEventListener('calendar:change', this.close.bind(this));
		}
	}

	_bindGlobalListeners() {
		if (this.config.closeOnBlur) {
			document.addEventListener('focusin', this._onBlurCheck);
		}
	}

	_unbindGlobalListeners() {
		document.removeEventListener('focusin', this._onBlurCheck);
	}

	_onCloserClick(event) {
		if (!event.target.closest(CLOSE_SELECTOR)) return;

		this.close();
	}

	/** Prevent mouse clicks from toggling closed while hover holds it open. */
	_onTriggerClick(event) {
		if (this.isOpen() && event.detail > 0) {
			event.preventDefault();
		}
	}

	_onMouseEnter() {
		this._clearHoverTimeout();
		this.open();
	}

	_onMouseLeave() {
		this.hoverTimeout = setTimeout(() => {
			this.close();
		}, 150);
	}

	_clearHoverTimeout() {
		if (this.hoverTimeout) {
			clearTimeout(this.hoverTimeout);
			this.hoverTimeout = null;
		}
	}

	/** Deferred: focusin fires before activeElement updates. */
	_onBlurCheck() {
		setTimeout(() => {
			const focused = document.activeElement;

			if (!focused || focused === document.body) return;

			const isInside = this.elNode.contains(focused);
			const isTrigger = this.elTriggers.some((t) => t.contains(focused));

			if (isInside || isTrigger) return;

			this.close();
		}, 0);
	}

	/** @param {ToggleEvent} event */
	_onToggle(event) {
		const isOpen = event.newState === 'open';

		this._updateTriggerState(isOpen);
		toggleHtmlClass(this.config.htmlStateClass, isOpen);

		if (!isOpen) {
			if (this.config.deeplink) updateHash(this.elNode.id, false);

			this._unbindGlobalListeners();
			this._dispatch('close');

			return;
		}

		if (this.config.deeplink) updateHash(this.elNode.id, true);

		this._bindGlobalListeners();
		this._dispatch('open');
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new Popover(node, config);

		module.init();

		return module;
	});
}
